import React from 'react';
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  ListItemText,
  MenuItem,
  Select,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import type { SxProps } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import styled from '@emotion/styled';

export type MultiSelectOption = {
  value: string;
  label: string;
  backgroundColor?: string;
};

type MultiSelectChipsProps = {
  id: string;
  name: string;
  value: string[];
  options: MultiSelectOption[];
  disabled?: boolean;
  onChange: (event: SelectChangeEvent<string[]>) => void;
  /** Id of the element that labels this control (accessibility). Pass the same id as the StyledLabelText above. */
  labelId: string;
  formControlSx?: SxProps<Theme>;
  selectSx?: SxProps<Theme>;
  chipVariant?: 'default' | 'primary' | 'secondary' | 'success' | 'warning';
};

const StyledChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== '$bgColor',
})<{ $bgColor?: string }>`
  ${(props) => props.$bgColor && `&& { background-color: ${props.$bgColor}; }`}
`;

export const MultiSelectChips = ({
  id,
  name,
  value,
  options,
  disabled = false,
  onChange,
  labelId,
  formControlSx,
  selectSx,
  chipVariant = 'secondary',
}: MultiSelectChipsProps) => {
  return (
    <FormControl sx={{ width: '100%', ...formControlSx }}>
      <Select
        disabled={disabled}
        size="small"
        name={name}
        id={id}
        labelId={labelId}
        value={value || []}
        multiple
        onChange={onChange}
        inputProps={{ 'aria-labelledby': labelId, id: `${id}-input` }}
        sx={{ width: '100%', ...(selectSx as any) }}
        renderValue={(selected) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {selected.map((selectedValue) => {
              const option = options.find((o) => o.value === selectedValue);
              const label = option?.label ?? selectedValue;
              return (
                <StyledChip
                  key={`${id}-selected-${selectedValue}`}
                  size="small"
                  label={label}
                  variant={chipVariant}
                  $bgColor={option?.backgroundColor}
                />
              );
            })}
          </Box>
        )}
      >
        {options.map((option) => (
          <MenuItem key={`${id}-option-${option.value}`} value={option.value}>
            <Checkbox checked={(value || []).indexOf(option.value) > -1} />
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
